import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTimeDisplay } from "../../components/date-time-display";
import { EmptyState, ErrorNotice, LoadingView } from "../../components/feedback";
import {
  PAGE_LIMITS,
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from "../../constants";
import { createApiPath, requestAdminApi } from "../../lib/api-client";
import type { SupportTicket, TicketList } from "../../types";

type TicketListScreenProps = {
  onOpenTicket: (ticketId: string) => void;
};

/**
 * 選択肢の値から表示名を得る
 *
 * @param options - 選択肢
 * @param value - 検索する値
 * @returns 対応する表示名
 */
const findOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
) => {
  return options.find((option) => option.value === value)?.label ?? value;
};

/**
 * 問い合わせ一覧画面
 *
 * @param props.onOpenTicket - 詳細を開く処理
 * @returns 問い合わせ一覧画面
 */
export const TicketListScreen = ({ onOpenTicket }: TicketListScreenProps) => {
  // 問い合わせ一覧を保持する
  const [tickets, setTickets] = useState<SupportTicket[]>([]);

  // 次ページのカーソルを保持する
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // 状態フィルターを保持する
  const [statusFilter, setStatusFilter] = useState("active");

  // 優先度フィルターを保持する
  const [priorityFilter, setPriorityFilter] = useState("");

  // 分類フィルターを保持する
  const [categoryFilter, setCategoryFilter] = useState("");

  // ページ内検索語を保持する
  const [searchQuery, setSearchQuery] = useState("");

  // 初回読み込み中かを保持する
  const [isLoading, setIsLoading] = useState(true);

  // 追加読み込み中かを保持する
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 利用者向けエラーを保持する
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 指定した条件で問い合わせを取得する
  const loadTickets = useCallback(
    async (cursor?: string) => {
      const isAdditionalPage = Boolean(cursor);

      if (isAdditionalPage) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      setErrorMessage(null);

      try {
        const path = createApiPath("/v1/admin/support/tickets", {
          status: statusFilter,
          priority: priorityFilter,
          category: categoryFilter,
          limit: PAGE_LIMITS.tickets,
          cursor,
        });
        const result = await requestAdminApi<TicketList>(path);

        setTickets((currentTickets) => {
          return isAdditionalPage ? [...currentTickets, ...result.tickets] : result.tickets;
        });
        setNextCursor(result.next_cursor);
      } catch (error: unknown) {
        console.error("Failed to load support tickets", error);
        setErrorMessage("お問い合わせを読み込めませんでした。");
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [categoryFilter, priorityFilter, statusFilter],
  );

  // ページ内検索に一致する問い合わせを絞り込む
  const visibleTickets = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase("ja-JP");

    if (!normalizedQuery) {
      return tickets;
    }

    return tickets.filter((ticket) => {
      const searchableText = [
        ticket.title ?? "",
        ticket.ticket_id,
        ticket.user?.display_name ?? "",
        ticket.latest_message_preview ?? "",
      ]
        .join(" ")
        .toLocaleLowerCase("ja-JP");

      return searchableText.includes(normalizedQuery);
    });
  }, [searchQuery, tickets]);

  // フィルター変更時に先頭から読み直す
  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">お問い合わせ管理</p>
          <h1>お問い合わせ</h1>
          <p>届いた内容を確認し、優先度をつけて返信できます。</p>
        </div>
        <button
          className="button button--secondary"
          type="button"
          disabled={isLoading}
          onClick={() => void loadTickets()}
        >
          {isLoading ? "更新中…" : "最新の状態に更新"}
        </button>
      </section>

      <section className="filter-panel" aria-label="お問い合わせの絞り込み">
        <label className="search-field">
          <span>ページ内検索</span>
          <input
            type="search"
            value={searchQuery}
            placeholder="件名・ID・ユーザーで検索"
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        <label>
          <span>状態</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="active">対応中すべて</option>
            {TICKET_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>優先度</span>
          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
          >
            <option value="">すべて</option>
            {TICKET_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>分類</span>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <option value="">すべて</option>
            {TICKET_CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div className="results-summary" aria-live="polite">
        <strong>{visibleTickets.length}件</strong>
        <span>このページに表示しています</span>
        {tickets.some((ticket) => ticket.unread_count > 0) ? (
          <span className="unread-legend">未読あり</span>
        ) : null}
      </div>

      {errorMessage ? (
        <ErrorNotice message={errorMessage} onRetry={() => void loadTickets()} />
      ) : null}

      {isLoading ? (
        <LoadingView label="お問い合わせを読み込んでいます" />
      ) : visibleTickets.length === 0 ? (
        <EmptyState
          title="該当するお問い合わせはありません"
          description="絞り込み条件や検索語を変えてお試しください。"
        />
      ) : (
        <section className="table-card" aria-label="お問い合わせ一覧">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>お問い合わせ</th>
                  <th>状態</th>
                  <th>分類</th>
                  <th>優先度</th>
                  <th>最終更新</th>
                </tr>
              </thead>
              <tbody>
                {visibleTickets.map((ticket) => (
                  <tr
                    key={ticket.ticket_id}
                    className={ticket.unread_count > 0 ? "table-row--unread" : undefined}
                  >
                    <td>
                      <button
                        className="ticket-link"
                        type="button"
                        onClick={() => onOpenTicket(ticket.ticket_id)}
                      >
                        <span className="ticket-link__subject">
                          {ticket.unread_count > 0 ? (
                            <span className="unread-dot" role="img" aria-label="未読あり" />
                          ) : null}
                          {ticket.title || "件名なし"}
                        </span>
                        <span>{ticket.user?.display_name || "名前未登録"}</span>
                        {ticket.latest_message_preview ? (
                          <span>{ticket.latest_message_preview}</span>
                        ) : null}
                        <small>管理番号: {ticket.ticket_id}</small>
                      </button>
                    </td>
                    <td>
                      <span className={`badge badge--${ticket.status}`}>
                        {findOptionLabel(TICKET_STATUS_OPTIONS, ticket.status)}
                      </span>
                    </td>
                    <td>{findOptionLabel(TICKET_CATEGORY_OPTIONS, ticket.category)}</td>
                    <td>
                      <span className={`priority priority--${ticket.priority}`}>
                        {findOptionLabel(TICKET_PRIORITY_OPTIONS, ticket.priority)}
                      </span>
                    </td>
                    <td>
                      <DateTimeDisplay value={ticket.updated_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {nextCursor ? (
        <div className="load-more">
          <button
            className="button button--secondary"
            type="button"
            disabled={isLoadingMore}
            onClick={() => void loadTickets(nextCursor)}
          >
            {isLoadingMore ? "読み込んでいます…" : "続きを読み込む"}
          </button>
        </div>
      ) : null}
    </div>
  );
};
